import { useState, useEffect, useRef } from "react";
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

/* ─── DATA ─────────────────────────────────────────────────────── */
const ticker = ["7 B2B Products", "$10M+ Revenue", "3 Global Markets", "20 Founders Per Cohort", "6 Weeks", "AU · UK · US"];
const outcomes = [
  { n: "01", title: "Validated Idea", sub: "Customer-tested, market-ready signal" },
  { n: "02", title: "Commercial Plan", sub: "Revenue model, pricing, positioning" },
  { n: "03", title: "Working MVP", sub: "Functional prototype, real users" },
  { n: "04", title: "Tech Roadmap", sub: "Full build plan with real specs" },
  { n: "05", title: "Investor Narrative", sub: "Deck-ready story that converts" },
  { n: "06", title: "90-Day Plan", sub: "Week-by-week execution path" },
];
const weeks = [
  { w: "01–02", title: "Idea Validation & Problem Sharpening", points: ["Define the core problem worth solving", "Customer persona deep-dive", "Validate demand fast", "Competitive landscape mapping"] },
  { w: "03–04", title: "Commercial Plan & Go-To-Market", points: ["Pricing & revenue model", "B2B sales strategy", "Early customer acquisition", "Pitch & storytelling"] },
  { w: "05",   title: "Tech Strategy & MVP Blueprint",    points: ["Vibe Coding fundamentals", "MVP scoping & build", "Tech stack decisions", "Prototype with real users"] },
  { w: "06",   title: "Dev Plan & Launch Readiness",      points: ["Full tech & dev roadmap", "First 90-day plan", "Investor & customer narrative", "Launch strategy"] },
];
const testimonials = [
  { quote: "I'd been sitting on this idea for two years. In six weeks I had a validated concept, a pitch deck, and a real prototype.", name: "Sarah K.", role: "Corporate Exec → SaaS Founder", initials: "SK" },
  { quote: "The commercial thinking is different when it comes from people who've actually sold B2B software. No fluff — just what works.", name: "James R.", role: "Side Hustle → First Paying Customers", initials: "JR" },
  { quote: "I thought I needed a technical co-founder. Turns out I just needed the right team for 6 weeks. Launched beta by the final week.", name: "Priya M.", role: "Idea Stage → Beta Launch", initials: "PM" },
];
const faq = [
  { q: "Do I need a technical background?", a: "No. The program is built for founders at any technical level. Our tech team handles the build; you focus on the problem and the market." },
  { q: "What if my idea isn't fully formed yet?", a: "That's exactly who this is for. Weeks 1–2 are specifically designed to sharpen and validate your thinking before you build anything." },
  { q: "Is $5,000 AUD the total cost?", a: "Yes. One flat fee covers all 6 weeks, sessions, office hours, tech team support, and everything in between. No upsells." },
  { q: "How are spots allocated?", a: "Every application is personally reviewed. We prioritise commitment and clarity of problem over stage or background." },
  { q: "What's the time commitment?", a: "Roughly 8–12 hours per week — one group session, one 1:1, and the work in between. It's intensive by design." },
];

/* ─── HOOKS ─────────────────────────────────────────────────────── */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return [ref, v];
}

function Reveal({ children, delay = 0, y = 32, style = {} }) {
  const [ref, v] = useInView();
  return (
    <div ref={ref} style={{
      opacity: v ? 1 : 0,
      transform: v ? "none" : `translateY(${y}px)`,
      transition: `opacity .8s ${delay}s cubic-bezier(.22,1,.36,1), transform .8s ${delay}s cubic-bezier(.22,1,.36,1)`,
      ...style
    }}>
      {children}
    </div>
  );
}

/* ─── TICKER ─────────────────────────────────────────────────────── */
function Ticker() {
  const items = [...ticker, ...ticker, ...ticker];
  return (
    <div style={{ overflow: "hidden", borderTop: "1px solid var(--gb)", borderBottom: "1px solid var(--gb)", padding: "14px 0", background: "rgba(250,204,21,.04)" }}>
      <div style={{ display: "flex", gap: 0, animation: "ticker 24s linear infinite", width: "max-content" }}>
        {items.map((t, i) => (
          <span key={i} style={{ fontFamily: "var(--sans)", fontSize: 11, letterSpacing: "0.22em", color: "var(--g)", padding: "0 40px", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 40 }}>
            {t}
            <span style={{ display: "inline-block", width: 4, height: 4, background: "var(--g)", borderRadius: "50%", opacity: 0.5 }} />
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── TAG ────────────────────────────────────────────────────────── */
function Tag({ children }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
      <div style={{ width: 32, height: 1, background: "var(--g)" }} />
      <span style={{ fontFamily: "var(--sans)", fontSize: 10, letterSpacing: "0.28em", color: "var(--g)", fontWeight: 500 }}>{children}</span>
    </div>
  );
}

/* ─── FORM SECTION ───────────────────────────────────────────────── */
function FormSection({ num, label, children }) {
  return (
    <div style={{ marginBottom: 64 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 36 }}>
        <span style={{ fontFamily: "var(--sans)", fontSize: 10, letterSpacing: ".22em", color: "var(--g)", fontWeight: 500, whiteSpace: "nowrap" }}>{num} — {label}</span>
        <div style={{ flex: 1, height: 1, background: "var(--gb)" }} />
      </div>
      {children}
    </div>
  );
}

/* ─── FAQ ────────────────────────────────────────────────────────── */
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {faq.map((item, i) => (
        <div key={i} style={{ borderBottom: "1px solid var(--gb)", overflow: "hidden" }}>
          <button onClick={() => setOpen(open === i ? null : i)} style={{
            width: "100%", background: "none", border: "none", cursor: "pointer",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "28px 0", color: "var(--c)", textAlign: "left", gap: 20,
          }}>
            <span style={{ fontFamily: "var(--serif)", fontSize: 19, fontWeight: 700 }}>{item.q}</span>
            <div style={{
              width: 28, height: 28, border: "1px solid",
              borderColor: open === i ? "var(--g)" : "var(--gb)",
              borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              transition: "all .35s", transform: open === i ? "rotate(45deg)" : "none",
            }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <line x1="6" y1="1" x2="6" y2="11" stroke={open === i ? "var(--g)" : "currentColor"} strokeWidth="1.2" />
                <line x1="1" y1="6" x2="11" y2="6" stroke={open === i ? "var(--g)" : "currentColor"} strokeWidth="1.2" />
              </svg>
            </div>
          </button>
          <div style={{ maxHeight: open === i ? 200 : 0, overflow: "hidden", transition: "max-height .45s cubic-bezier(.22,1,.36,1)" }}>
            <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8, paddingBottom: 28, maxWidth: 680 }}>{item.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── MAIN ──────────────────────────────────────────────────────── */
export default function KPL() {
  const [navSolid, setNavSolid] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [stage, setStage] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    idea: '',
    ideaDescription: '',
    doneSoFar: '',
    urgency: '',
    timeAvailability: '',
    anythingElse: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fn = () => { setScrollY(window.scrollY); setNavSolid(window.scrollY > 60); };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
    }
  };

  const handleSubmit = async () => {
    // Mandatory fields: About You (firstName, lastName, email, phone, country) + Your Idea (idea, ideaDescription, doneSoFar)
    const required = {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email Address',
      phone: 'Phone Number',
      country: 'Country / Location',
      idea: 'Your idea in one sentence',
      ideaDescription: 'Problem description',
      doneSoFar: 'What you\'ve done so far',
    };

    const errors = {};
    for (const [key, label] of Object.entries(required)) {
      if (!formData[key] || !formData[key].trim()) {
        errors[key] = `${label} is required`;
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitMessage('Please fill in all required fields marked with *');
      // Scroll to first error
      const firstErrorField = document.querySelector('[data-error="true"]');
      if (firstErrorField) firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          country: formData.country,
          idea: formData.idea,
          ideaDescription: formData.ideaDescription,
          doneSoFar: formData.doneSoFar,
          stage: stage !== null ? ["Pre-revenue idea stage", "Side hustle → Full time", "Corporate exec with an idea", "Early-stage startup"][stage] : '',
          commitment: formData.timeAvailability,
          urgency: formData.urgency,
          anythingElse: formData.anythingElse
        }),
      });

      if (response.ok) {
        setSubmitMessage('Application submitted successfully! You will receive a confirmation email shortly.');
        setFieldErrors({});
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          country: '',
          idea: '',
          ideaDescription: '',
          doneSoFar: '',
          urgency: '',
          timeAvailability: '',
          anythingElse: ''
        });
        setStage(null);
      } else {
        setSubmitMessage('Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitMessage('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const parallax = -scrollY * 0.16;

  return (
    <div style={{ background: "#080808", color: "#ffffff", fontFamily: "var(--sans)", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=Instrument+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --g: #facc15;   /* yellow-400 */
          --g5: #eab308;  /* yellow-500 */
          --gl: #fde68a;  /* yellow-200 hover */
          --gb: rgba(234,179,8,.25);
          --gdim: rgba(234,179,8,.08);
          --c: #ffffff;
          --m: #9ca3af;   /* gray-400 */
          --f: rgba(255,255,255,.06);
          --serif: 'Playfair Display', Georgia, serif;
          --sans: 'Instrument Sans', sans-serif;
          --max: 1220px;
        }
        html { scroll-behavior: smooth; }
        ::selection { background: var(--gdim); color: var(--gl); }
        @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-33.333%)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:none} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
        @keyframes blinkGlow { 0%,100%{opacity:1;box-shadow:0 0 0 rgba(250,204,21,0)} 50%{opacity:.72;box-shadow:0 0 24px rgba(250,204,21,.22)} }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: var(--gb); }
        input, textarea, select {
          background: transparent; border: none;
          border-bottom: 1px solid var(--gb);
          color: var(--c); font-family: var(--sans); font-size: 14px; font-weight: 400;
          padding: 14px 0; width: 100%; outline: none; transition: border-color .3s;
          appearance: none;
        }
        input::placeholder, textarea::placeholder { color: var(--m); }
        input:focus, textarea:focus, select:focus { border-bottom-color: var(--g); }
        select option { background: #0e0e0e; }
        textarea { resize: vertical; }
        .gl:hover { color: var(--g) !important; transition: color .25s; }
      `}</style>

      {/* ══ NAVBAR ═══════════════════════════════════════════════════ */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "nowrap",
        padding: "20px 40px",
        background: navSolid ? "rgba(8,8,8,.94)" : "transparent",
        borderBottom: navSolid ? "1px solid var(--gb)" : "1px solid transparent",
        backdropFilter: navSolid ? "blur(18px)" : "none",
        transition: "background .5s, border-color .5s, backdrop-filter .5s"
      }}>
        {/* Logo + wordmark */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <img
            src="/logo.svg"
            alt="KPL Ventures Logo"
            style={{
              width: 56,
              height: 56,
              objectFit: "contain",
              display: "block",
              filter: "brightness(0) saturate(100%) invert(82%) sepia(42%) saturate(1198%) hue-rotate(352deg) brightness(102%) contrast(96%) drop-shadow(0 0 8px rgba(250,204,21,.25))"
            }}
          />
          <span style={{
            fontFamily: "var(--sans)", fontSize: 12, letterSpacing: ".2em", color: "var(--g)", fontWeight: 500, whiteSpace: "nowrap"
          }}>KPL VENTURES</span>
        </div>

        {/* Nav links — use onClick + scrollIntoView for iframe compatibility */}
        <div style={{ display: "flex", alignItems: "center", gap: 32, flexShrink: 0 }}>
          {[["Program","program"],["Who It's For","whoitsfor"],["Investment","investment"]].map(([l,id],i) => (
            <button key={i} onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
              className="gl"
              style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", background: "none", border: "none", cursor: "pointer", whiteSpace: "nowrap", padding: 0 }}
            >{l}</button>
          ))}
          {user ? (
            <>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600,
                  padding: "8px 16px", background: "var(--g)", color: "#000",
                  border: "none", cursor: "pointer", whiteSpace: "nowrap",
                  transition: "background .2s, transform .2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gl)"; e.currentTarget.style.transform = "scale(1.03)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--g)"; e.currentTarget.style.transform = "none"; }}
              >
                DASHBOARD
              </button>
              <button
                onClick={async () => {
                  await logout();
                  navigate('/');
                }}
                style={{
                  fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600,
                  padding: "8px 16px", background: "transparent", color: "var(--m)",
                  border: "1px solid var(--gb)", cursor: "pointer", whiteSpace: "nowrap",
                  transition: "all .2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "var(--c)"; e.currentTarget.style.borderColor = "var(--g)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "var(--m)"; e.currentTarget.style.borderColor = "var(--gb)"; }}
              >
                LOGOUT
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/signin')}
                style={{
                  fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600,
                  padding: "8px 16px", background: "transparent", color: "var(--m)",
                  border: "1px solid var(--gb)", cursor: "pointer", whiteSpace: "nowrap",
                  transition: "all .2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "var(--c)"; e.currentTarget.style.borderColor = "var(--g)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "var(--m)"; e.currentTarget.style.borderColor = "var(--gb)"; }}
              >
                SIGN IN
              </button>
              <button
                onClick={() => navigate('/signup')}
                style={{
                  fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600,
                  padding: "8px 16px", background: "var(--g)", color: "#000",
                  border: "none", cursor: "pointer", whiteSpace: "nowrap",
                  transition: "background .2s, transform .2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gl)"; e.currentTarget.style.transform = "scale(1.03)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--g)"; e.currentTarget.style.transform = "none"; }}
              >
                SIGN UP
              </button>
            </>
          )}
        </div>
      </nav>

            {/* ══ HERO ═════════════════════════════════════════════════════ */}
      <section style={{ minHeight: "100vh", display: "flex", alignItems: "center", padding: "120px 24px 80px", position: "relative", overflow: "hidden" }}>

        {/* Subtle grid bg */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: `repeating-linear-gradient(0deg,transparent,transparent 79px,var(--f) 80px),repeating-linear-gradient(90deg,transparent,transparent 79px,var(--f) 80px)`, opacity: .5 }} />
        {/* Gold glow */}
        <div style={{ position: "absolute", top: "20%", right: "8%", width: 480, height: 480, background: "radial-gradient(ellipse,rgba(250,204,21,.06) 0%,transparent 70%)", pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: "var(--max)", width: "100%", margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>

          {/* LEFT */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 32, animation: "fadeUp .8s .05s both" }}>
              <div style={{ width: 8, height: 8, background: "var(--g)", animation: "pulse 2s infinite" }} />
              <span style={{ fontFamily: "var(--sans)", fontSize: 11, letterSpacing: ".28em", color: "var(--g)", fontWeight: 500 }}>6-WEEK FOUNDER ACCELERATOR</span>
            </div>

            <h1 style={{ fontFamily: "var(--serif)", fontWeight: 900, lineHeight: 1.0, animation: "fadeUp .9s .1s both", marginBottom: 24 }}>
              <div style={{ fontSize: "clamp(46px, 5.5vw, 76px)", color: "var(--c)" }}>TURN YOUR</div>
              <div style={{ fontSize: "clamp(46px, 5.5vw, 76px)", color: "var(--c)" }}>IDEA INTO A</div>
              <div style={{ fontSize: "clamp(46px, 5.5vw, 76px)", color: "var(--g5)" }}>REAL BUSINESS</div>
            </h1>

            <p style={{ fontFamily: "var(--sans)", fontSize: 16, color: "var(--m)", maxWidth: 460, lineHeight: 1.75, marginBottom: 40, animation: "fadeUp .9s .2s both" }}>
              Work directly with a founder who has built and commercialised multiple B2B products across AU, UK, and US.
            </p>

            <div style={{ display: "flex", alignItems: "center", gap: 28, animation: "fadeUp .9s .3s both" }}>
              <button onClick={() => document.getElementById("applyform")?.scrollIntoView({ behavior: "smooth" })} style={{
                display: "inline-flex", alignItems: "center", gap: 12,
                padding: "15px 36px", background: "var(--g)", color: "#080808",
                fontFamily: "var(--sans)", fontSize: 13, fontWeight: 700, letterSpacing: ".12em",
                border: "none", cursor: "pointer", transition: "all .25s", borderRadius: 2
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gl)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--g)"; e.currentTarget.style.transform = "none"; }}
              >APPLY NOW</button>
              <button onClick={() => document.getElementById("program")?.scrollIntoView({ behavior: "smooth" })}
                className="gl"
                style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: 0 }}>
                See the program <span>↓</span>
              </button>
            </div>
          </div>

          {/* RIGHT — 2×2 stats grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, animation: "fadeUp 1s .35s both" }}>
            {[
              { value: "7",     label: "B2B Products" },
              { value: "3",     label: "Markets" },
              { value: "$10M+", label: "Revenue" },
              { value: "20",    label: "Founders" },
            ].map((s, i) => (
              <div key={i} style={{
                padding: "36px 28px",
                border: "1px solid var(--gb)",
                background: i === 0 ? "var(--gdim)" : "rgba(255,255,255,.02)",
                transition: "all .3s", cursor: "default"
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gdim)"; e.currentTarget.style.borderColor = "var(--g)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = i === 0 ? "var(--gdim)" : "rgba(255,255,255,.02)"; e.currentTarget.style.borderColor = "var(--gb)"; }}
              >
                <div style={{ fontFamily: "var(--serif)", fontSize: 52, fontWeight: 900, color: "var(--g)", lineHeight: 1, marginBottom: 8 }}>{s.value}</div>
                <div style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--m)" }}>{s.label}</div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ══ TICKER ═══════════════════════════════════════════════════ */}
      <Ticker />

      {/* ══ WHO IT'S FOR — LARGE TYPE LIST ═══════════════════════════ */}
      <section id="whoitsfor" style={{ padding: "90px 24px", borderBottom: "1px solid var(--gb)" }}>
        <div style={{ maxWidth: "var(--max)", margin: "0 auto", display: "grid", gridTemplateColumns: "260px 1fr", gap: 80 }}>
          <Reveal>
            <div style={{ paddingTop: 8 }}>
              <Tag>WHO IT'S FOR</Tag>
              <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8 }}>
                If you see yourself below, this program was built for you.
              </p>
            </div>
          </Reveal>

          <div>
            {[
              { title: "Pre-Revenue Idea Stage", desc: "You have a problem worth solving but need validation before building anything." },
              { title: "Side Hustle → Full Time", desc: "You want to turn your side project into a real, revenue-generating business." },
              { title: "Corporate Professionals",  desc: "You've identified a gap in your industry and want to build around it." },
              { title: "Early-Stage Startups",     desc: "You have a product but need traction, positioning, and growth clarity." },
            ].map((item, i) => (
              <Reveal key={i} delay={i * .09}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 28, padding: "36px 0", borderBottom: "1px solid var(--gb)", cursor: "default" }}
                  onMouseEnter={e => { e.currentTarget.querySelector(".htitle").style.color = "var(--g)"; }}
                  onMouseLeave={e => { e.currentTarget.querySelector(".htitle").style.color = "var(--c)"; }}
                >
                  <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "var(--m)", minWidth: 22, flexShrink: 0, paddingTop: 6 }}>0{i+1}</span>
                  <div style={{ flex: 1 }}>
                    <h3 className="htitle" style={{ fontFamily: "var(--serif)", fontSize: "clamp(20px, 2.2vw, 28px)", fontWeight: 700, lineHeight: 1.2, transition: "color .3s", marginBottom: 8 }}>{item.title}</h3>
                    <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.7 }}>{item.desc}</p>
                  </div>
                  <span style={{ fontSize: 20, color: "var(--gb)", alignSelf: "center" }}>→</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PROGRAM — HORIZONTAL SCROLL ══════════════════════════════ */}
      <section id="program" style={{ padding: "120px 0", borderBottom: "1px solid var(--gb)", overflow: "hidden" }}>
        <div style={{ padding: "0 24px", maxWidth: "var(--max)", margin: "0 auto 40px" }}>
          <Reveal>
            <Tag>THE PROGRAM</Tag>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 24 }}>
              <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(40px, 4.5vw, 64px)", fontWeight: 900, lineHeight: .95 }}>
                6 Weeks.<br /><span style={{ color: "var(--g5)", fontStyle: "normal" }}>Built Around You.</span>
              </h2>
              <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", maxWidth: 320, lineHeight: 1.8 }}>
                Every week ends with something tangible, not homework. Cohort 1 kicks off July, 2026.
              </p>
            </div>
          </Reveal>
        </div>

        {/* Full width cards */}
        <div style={{ display: "flex", width: "100vw", position: "relative", left: "50%", right: "50%", marginLeft: "-50vw", marginRight: "-50vw" }}>
          {weeks.map((w, i) => (
            <Reveal key={i} delay={i * .08} style={{ flex: 1, scrollSnapAlign: "start" }}>
              <div style={{
                border: "1px solid var(--gb)", padding: "40px 32px",
                height: 380, display: "flex", flexDirection: "column",
                background: "rgba(255,255,255,.015)", transition: "all .4s", position: "relative", overflow: "hidden"
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gdim)"; e.currentTarget.style.borderColor = "var(--g)"; e.currentTarget.style.transform = "translateY(-6px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.015)"; e.currentTarget.style.borderColor = "var(--gb)"; e.currentTarget.style.transform = "none"; }}
              >
                <div style={{ fontFamily: "var(--sans)", fontSize: 10, letterSpacing: ".22em", color: "var(--g)", marginBottom: 20, fontWeight: 500 }}>WEEK {w.w}</div>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700, lineHeight: 1.3, marginBottom: 28, flex: 1 }}>{w.title}</h3>
                <div style={{ width: 32, height: 1, background: "var(--g)", opacity: .5, marginBottom: 26 }} />
                <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 13 }}>
                  {w.points.map((p, j) => (
                    <li key={j} style={{ display: "flex", gap: 12, fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", alignItems: "flex-start", lineHeight: 1.5 }}>
                      <span style={{ color: "var(--g)", flexShrink: 0, marginTop: 1 }}>↳</span> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ══ WHAT YOU LEAVE WITH — 2-col list ═════════════════════════ */}
      <section style={{ padding: "90px 24px", borderBottom: "1px solid var(--gb)" }}>
        <div style={{ maxWidth: "var(--max)", margin: "0 auto" }}>
          <Reveal>
            <Tag>WHAT YOU LEAVE WITH</Tag>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(40px, 4.5vw, 64px)", fontWeight: 900, lineHeight: .95, marginBottom: 60 }}>
              Real Outputs.<br /><span style={{ color: "var(--g5)", fontStyle: "normal" }}>Not Just Advice.</span>
            </h2>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
            {outcomes.map((o, i) => (
              <Reveal key={i} delay={i * .06}>
                <div style={{
                  padding: "44px 40px",
                  borderTop: "1px solid var(--gb)",
                  borderRight: i % 2 === 0 ? "1px solid var(--gb)" : "none",
                  display: "flex", gap: 28, alignItems: "flex-start",
                  transition: "background .35s", cursor: "default"
                }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--gdim)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{ fontFamily: "var(--serif)", fontSize: 12, color: "var(--g)", fontWeight: 700, paddingTop: 7, minWidth: 22, opacity: .55 }}>{o.n}</span>
                  <div>
                    <h3 style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>{o.title}</h3>
                    <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", lineHeight: 1.7 }}>{o.sub}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div style={{ borderTop: "1px solid var(--gb)" }} />
        </div>
      </section>

      {/* ══ WHY THIS WORKS — SPLIT SECTION ══════════════════════════ */}
      <section style={{ padding: "90px 24px", borderBottom: "1px solid var(--gb)", background: "rgba(234,179,8,.025)" }}>
        <div style={{ maxWidth: "var(--max)", margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 96, alignItems: "center" }}>
          <Reveal>
            <Tag>WHY THIS WORKS</Tag>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(38px, 4vw, 58px)", fontWeight: 900, lineHeight: 1.05, marginBottom: 28 }}>
              Not Consultants.<br />Not Academics.<br /><span style={{ color: "var(--g5)", fontStyle: "normal" }}>Operators.</span>
            </h2>
            <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8, marginBottom: 16 }}>
              Every session is led by founders who've built, sold, and scaled B2B software across three markets. We've made the expensive mistakes so you don't have to.
            </p>
            <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8 }}>
              Plus a hands-on tech team for Vibe Coding, prototyping, and MVP builds — so you leave with something real, not a strategy deck.
            </p>
          </Reveal>

          <Reveal delay={.15}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[
                { sym: "◈", stat: "$10M+", label: "In B2B Revenue Generated", detail: "Across AU, UK and US markets" },
                { sym: "◎", stat: "7",     label: "Software Products Built",   detail: "From idea to commercial launch" },
                { sym: "◉", stat: "3",     label: "International Markets",     detail: "Operating across three continents" },
                { sym: "◐", stat: "20",    label: "Founders Per Cohort",       detail: "Intentionally small, high-touch" },
              ].map((p, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 24, padding: "26px 28px",
                  border: "1px solid var(--gb)", background: "rgba(255,255,255,.015)", transition: "all .3s"
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--g)"; e.currentTarget.style.background = "var(--gdim)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--gb)"; e.currentTarget.style.background = "rgba(255,255,255,.015)"; }}
                >
                  <span style={{ color: "var(--g)", fontSize: 20, fontFamily: "var(--serif)", flexShrink: 0 }}>{p.sym}</span>
                  <div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
                      <span style={{ fontFamily: "var(--serif)", fontSize: 26, fontWeight: 700, color: "var(--g)" }}>{p.stat}</span>
                      <span style={{ fontFamily: "var(--sans)", fontSize: 12, color: "var(--c)", fontWeight: 500 }}>{p.label}</span>
                    </div>
                    <div style={{ fontFamily: "var(--sans)", fontSize: 12, color: "var(--m)" }}>{p.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ PULL QUOTE ════════════════════════════════════════════════ */}
      <section style={{ padding: "80px 24px", borderBottom: "1px solid var(--gb)", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "var(--g)", opacity: .45 }} />
        <Reveal y={16}>
          <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center" }}>
            <p style={{ fontFamily: "var(--serif)", fontSize: "clamp(20px, 2.2vw, 30px)", fontStyle: "normal", lineHeight: 1.65, color: "var(--c)", marginBottom: 32 }}>
              "Most programs give you frameworks. We give you the hard-won lessons from $10M+ in B2B revenue — and the tech team to build what you design."
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14 }}>
              <div style={{ width: 1, height: 28, background: "var(--g)", opacity: .5 }} />
              <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "var(--g)", letterSpacing: ".15em", fontWeight: 500 }}>KPL VENTURES · LEAD FOUNDER</span>
              <div style={{ width: 1, height: 28, background: "var(--g)", opacity: .5 }} />
            </div>
          </div>
        </Reveal>
      </section>

      {/* ══ WHO YOU'RE WORKING WITH ══════════════════════════════════ */}
      <section style={{ padding: "90px 24px", borderBottom: "1px solid var(--gb)" }}>
        <div style={{ maxWidth: "var(--max)", margin: "0 auto" }}>

          {/* Section label + big headline */}
          <Reveal>
            <Tag>WHO YOU'RE WORKING WITH</Tag>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(44px, 6vw, 80px)", fontWeight: 900, lineHeight: .93, marginBottom: 64 }}>
              You Get <span style={{ color: "var(--g5)" }}>Me.</span><br />Not an Offsider.
            </h2>
          </Reveal>

          {/* Two-column: photo left, content right */}
          <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 64, alignItems: "flex-start" }}>

            {/* LEFT — photo + credentials */}
            <Reveal>
              <div>
                {/* Founder photo — replace src with your actual image e.g. "/founder.jpg" */}
                <div style={{
                  width: "100%", aspectRatio: "3/4",
                  border: "1px solid var(--gb)", marginBottom: 28,
                  position: "relative", overflow: "hidden", background: "#111"
                }}>
                  <img
                    src="./founderphoto.jpeg"
                    alt="Founder photo"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: .85 }}
                  />
                  {/* Corner accents overlay */}
                  <div style={{ position: "absolute", top: 10, left: 10, width: 20, height: 20, borderTop: "2px solid var(--g)", borderLeft: "2px solid var(--g)", opacity: .6, pointerEvents: "none" }} />
                  <div style={{ position: "absolute", bottom: 10, right: 10, width: 20, height: 20, borderBottom: "2px solid var(--g)", borderRight: "2px solid var(--g)", opacity: .6, pointerEvents: "none" }} />
                </div>

                {/* Credentials */}
                <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", lineHeight: 1.8, marginTop: 4 }}>
                  Anil Patel is the Co-founder, Chairman of Virtual Mgr. He brings 2 Decades of experience scaling businesses across multiple continents. From corporate strategy roles to building Virtual Mgr into a global operation, he's lived the entrepreneur's journey from startup chaos to sustainable growth. His superpower is turning operational complexity into competitive advantage.
                </p>
              </div>
            </Reveal>

            {/* RIGHT — quote + bio */}
            <Reveal delay={.15}>
              <div>
                {/* Block quote */}
                <div style={{ borderLeft: "3px solid var(--g)", paddingLeft: 28, marginBottom: 40 }}>
                  <p style={{ fontFamily: "var(--serif)", fontSize: "clamp(16px, 1.6vw, 20px)", lineHeight: 1.7, color: "var(--c)", fontStyle: "italic" }}>
                    "Every session is with me. Every vetting call is with me. Every tough question about your idea — that's me asking it. You're not paying for access to a program. You're paying for direct access to someone who's been in the trenches and built real things that real businesses pay for."
                  </p>
                </div>

                {/* Bio paragraphs */}
                <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 48 }}>
                  <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8 }}>
                    I started KPL Ventures because I kept seeing the same thing: smart founders with genuinely good ideas, burning time and money in the wrong direction because they didn't have the right person in their corner early enough.
                  </p>
                  <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8 }}>
                    My first company was born out of frustration. I was working in enterprise software and saw businesses paying six figures for tools that barely worked. I quit, maxed out a credit card, and built a leaner product that did the same job at a fraction of the cost. Within 18 months we had paying customers across two countries.
                  </p>
                  <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8 }}>
                    That experience taught me what no course or accelerator ever could — how to sell to business buyers who don't return your emails, how to price when you have zero brand recognition, and how to build only the features that actually close deals. I went on to replicate that playbook six more times, across industries like logistics, legal tech, and workforce management.
                  </p>
                  <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8 }}>
                    I've personally built and commercialised seven B2B software products across Australia, the UK and the US. I know what it looks like when an idea has legs and when it doesn't. I know how to price, how to sell to business buyers, and how to scope an MVP that doesn't take two years to build.
                  </p>
                  <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8 }}>
                    Along the way I made every expensive mistake in the book — hiring too fast, building features nobody asked for, chasing markets that weren't ready. Those lessons cost millions. They're now baked into every week of this program so you don't repeat them.
                  </p>
                  <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8 }}>
                    This program is small by design. Twenty companies. Every call, every review, every vetting conversation — handled personally by me. That's the whole point.
                  </p>
                </div>

                {/* Founder sign-off */}
                <div style={{ borderTop: "1px solid var(--gb)", paddingTop: 28 }}>
                  <div style={{ fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600, color: "var(--g)", letterSpacing: ".12em" }}>FOUNDER, KPL VENTURES</div>
                  <div style={{ fontFamily: "var(--serif)", fontSize: 14, color: "var(--m)", fontStyle: "italic", marginTop: 6 }}>— Anil Patel</div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Portfolio Startups */}
          <Reveal delay={.1}>
            <div style={{ marginTop: 64 }}>
              <Tag>FROM THE PORTFOLIO</Tag>
              <h3 style={{ fontFamily: "var(--serif)", fontSize: "clamp(24px, 2.5vw, 36px)", fontWeight: 900, lineHeight: 1.05, marginBottom: 36 }}>
                3 Startups. <span style={{ color: "var(--g5)" }}>Built & Scaled.</span>
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                {[
                  {
                    name: "Metacare",
                    domain: "Healthcare Technology",
                    desc: "A Brisbane-based platform that simplifies the GP referral process — an all-in-one tool for GPs to send Allied Health referrals fast and at no cost. Programs designed to ensure the right patient is seen by the right practitioner.",
                    stat: "GP",
                    statLabel: "REFERRALS",
                    market: "AU"
                  },
                  {
                    name: "iXCricket",
                    domain: "Sports Science",
                    desc: "A sports science company focused on improving cricket performance. Provides players with critical data to minimise injury risk, optimise performance, and reach their full potential through cutting-edge science and technology.",
                    stat: "DATA",
                    statLabel: "DRIVEN",
                    market: "GLOBAL"
                  },
                  {
                    name: "Acendis",
                    domain: "Data Science & Fintech",
                    desc: "Turns complex big data into clear, actionable insights for smarter financial decisions. Builds intelligent tools that analyse financial behaviours, measure affordability, and reduce risk — empowering property managers, lenders, and consumers.",
                    stat: "AI",
                    statLabel: "FINTECH",
                    market: "AU"
                  }
                ].map((s, i) => (
                  <div key={i} style={{
                    border: "1px solid var(--gb)", padding: "36px 32px",
                    background: "rgba(255,255,255,.015)", display: "flex", flexDirection: "column",
                    justifyContent: "space-between", minHeight: 300, transition: "all .4s", position: "relative", overflow: "hidden"
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--gdim)"; e.currentTarget.style.borderColor = "var(--g)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.015)"; e.currentTarget.style.borderColor = "var(--gb)"; e.currentTarget.style.transform = "none"; }}
                  >
                    <div>
                      <div style={{ fontFamily: "var(--sans)", fontSize: 10, letterSpacing: ".22em", color: "var(--g)", marginBottom: 8, fontWeight: 500 }}>{s.domain.toUpperCase()}</div>
                      <h4 style={{ fontFamily: "var(--serif)", fontSize: 24, fontWeight: 700, marginBottom: 16 }}>{s.name}</h4>
                      <div style={{ width: 24, height: 1, background: "var(--g)", opacity: .4, marginBottom: 16 }} />
                      <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", lineHeight: 1.75 }}>{s.desc}</p>
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginTop: 28, paddingTop: 20, borderTop: "1px solid var(--gb)" }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <span style={{ fontFamily: "var(--serif)", fontSize: 28, fontWeight: 900, color: "var(--g)" }}>{s.stat}</span>
                        <span style={{ fontFamily: "var(--sans)", fontSize: 10, letterSpacing: ".15em", color: "var(--m)" }}>{s.statLabel}</span>
                      </div>
                      <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "var(--m)", letterSpacing: ".1em" }}>{s.market}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Bottom stats bar */}
          <Reveal delay={.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, marginTop: 64 }}>
              {[
                { value: "7",     label: "B2B APPS\nCOMMERCIALISED" },
                { value: "3",     label: "INTERNATIONAL\nMARKETS" },
                { value: "$10M+", label: "REVENUE\nGENERATED" },
                { value: "20",    label: "FOUNDERS PER\nCOHORT — MAX" },
              ].map((s, i) => (
                <div key={i} style={{
                  padding: "36px 28px", textAlign: "center",
                  borderTop: "1px solid var(--gb)",
                  borderLeft: i > 0 ? "1px solid var(--gb)" : "1px solid var(--gb)",
                  borderRight: i === 3 ? "1px solid var(--gb)" : "none",
                  borderBottom: "1px solid var(--gb)",
                  background: "rgba(255,255,255,.015)", transition: "background .3s"
                }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--gdim)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,.015)"}
                >
                  <div style={{ fontFamily: "var(--serif)", fontSize: 52, fontWeight: 900, color: "var(--g)", lineHeight: 1, marginBottom: 12 }}>{s.value}</div>
                  <div style={{ fontFamily: "var(--sans)", fontSize: 11, color: "var(--m)", letterSpacing: ".12em", lineHeight: 1.5, whiteSpace: "pre-line" }}>{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>

        </div>
      </section>

      {/* ══ TESTIMONIALS — MAGAZINE LAYOUT ═══════════════════════════ */}
      <section style={{ padding: "90px 24px", borderBottom: "1px solid var(--gb)" }}>
        <div style={{ maxWidth: "var(--max)", margin: "0 auto" }}>
          <Reveal>
            <Tag>FROM THE COHORT</Tag>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(40px, 4.5vw, 64px)", fontWeight: 900, lineHeight: .95, marginBottom: 60 }}>
              Founders Who<br /><span style={{ color: "var(--g5)", fontStyle: "normal" }}>Made the Move.</span>
            </h2>
          </Reveal>

          {/* Gold feature + side stack */}
          <Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              {/* Feature */}
              <div style={{ padding: "60px 52px", background: "var(--g)", display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 320 }}>
                <div style={{ fontFamily: "var(--serif)", fontSize: 56, color: "rgba(8,8,8,.25)", lineHeight: 1, fontWeight: 900 }}>"</div>
                <p style={{ fontFamily: "var(--serif)", fontSize: "clamp(16px, 1.6vw, 20px)", lineHeight: 1.7, color: "#080808", fontStyle: "normal", fontWeight: 700 }}>
                  {testimonials[0].quote}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 36 }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(8,8,8,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--sans)", fontSize: 11, color: "#080808", fontWeight: 600 }}>{testimonials[0].initials}</div>
                  <div>
                    <div style={{ fontFamily: "var(--sans)", fontSize: 12, fontWeight: 600, color: "#080808" }}>{testimonials[0].name}</div>
                    <div style={{ fontFamily: "var(--sans)", fontSize: 11, color: "rgba(8,8,8,.6)", marginTop: 2 }}>{testimonials[0].role}</div>
                  </div>
                </div>
              </div>

              {/* Stack */}
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {testimonials.slice(1).map((t, i) => (
                  <div key={i} style={{
                    flex: 1, padding: "36px 40px", border: "1px solid var(--gb)",
                    background: "rgba(255,255,255,.015)", display: "flex", flexDirection: "column", justifyContent: "space-between",
                    transition: "all .3s"
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--gdim)"; e.currentTarget.style.borderColor = "var(--g)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.015)"; e.currentTarget.style.borderColor = "var(--gb)"; }}
                  >
                    <p style={{ fontFamily: "var(--serif)", fontSize: 15, lineHeight: 1.75, color: "var(--c)", fontStyle: "normal" }}>"{t.quote}"</p>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 22 }}>
                      <div style={{ width: 33, height: 33, borderRadius: "50%", border: "1px solid var(--gb)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--sans)", fontSize: 10, color: "var(--g)", fontWeight: 600 }}>{t.initials}</div>
                      <div>
                        <div style={{ fontFamily: "var(--sans)", fontSize: 12, fontWeight: 500 }}>{t.name}</div>
                        <div style={{ fontFamily: "var(--sans)", fontSize: 11, color: "var(--m)", marginTop: 2 }}>{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ INVESTMENT ════════════════════════════════════════════════ */}
      <section id="investment" style={{ padding: "90px 24px", borderBottom: "1px solid var(--gb)" }}>
        <div style={{ maxWidth: "var(--max)", margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" }}>
          <Reveal>
            <Tag>INVESTMENT</Tag>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(40px, 4.5vw, 64px)", fontWeight: 900, lineHeight: .95, marginBottom: 28 }}>
              One Price.<br /><span style={{ color: "var(--g5)", fontStyle: "normal" }}>No Hidden Extras.</span>
            </h2>
            <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8, marginBottom: 14 }}>
              One flat fee covers all 6 weeks — sessions, office hours, tech team access, MVP support, and everything in between.
            </p>
            <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8 }}>
              Places are limited to 20 companies per cohort. Every application is personally reviewed.
            </p>
          </Reveal>

          <Reveal delay={.15}>
            <div style={{ border: "1px solid var(--g)", padding: "52px 46px", position: "relative", background: "rgba(234,179,8,.03)" }}>
              {/* Corner accents */}
              {[{t:-2,l:-2,bt:"3px solid var(--g)",bl:"3px solid var(--g)"},{t:-2,r:-2,bt:"3px solid var(--g)",br:"3px solid var(--g)"},{b:-2,l:-2,bb:"3px solid var(--g)",bl:"3px solid var(--g)"},{b:-2,r:-2,bb:"3px solid var(--g)",br:"3px solid var(--g)"}].map((c,i)=>(
                <div key={i} style={{ position:"absolute", width:20, height:20, top:c.t, bottom:c.b, left:c.l, right:c.r, borderTop:c.bt, borderBottom:c.bb, borderLeft:c.bl, borderRight:c.br }} />
              ))}

              <div style={{ fontFamily: "var(--sans)", fontSize: 10, letterSpacing: ".22em", color: "var(--g)", marginBottom: 22, fontWeight: 500 }}>6-WEEK PROGRAM · COHORT 01</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
                <span style={{ fontFamily: "var(--serif)", fontSize: 64, fontWeight: 900, lineHeight: 1, color: "var(--g)" }}>$5,000</span>
                <span style={{ fontFamily: "var(--sans)", fontSize: 12, color: "var(--m)" }}>AUD</span>
              </div>
              <div style={{ fontFamily: "var(--sans)", fontSize: 13, color: "var(--m)", marginBottom: 38 }}>Per company · One-time · Payment split available</div>
              <div style={{ height: 1, background: "var(--gb)", marginBottom: 32 }} />

              {["6 weekly sessions, founder-led","Direct 1:1 office hours every week","MVP & tech team support included","Commercial & GTM strategy built in-session","Full tech & dev roadmap on completion","Access to the KPL founder network"].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "11px 0", borderBottom: i < 5 ? "1px solid rgba(234,179,8,.1)" : "none", fontFamily: "var(--sans)", fontSize: 14, color: "var(--c)" }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                    <circle cx="8" cy="8" r="7" stroke="var(--g)" strokeWidth="1"/>
                    <path d="M5 8l2 2 4-4" stroke="var(--g)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {item}
                </div>
              ))}

              <button onClick={() => document.getElementById("applyform")?.scrollIntoView({ behavior: "smooth" })} style={{
                display: "flex", alignItems: "center", justifyContent: "center", width: "100%",
                marginTop: 36, padding: "17px", background: "var(--g)", color: "#080808",
                fontFamily: "var(--sans)", fontSize: 11, fontWeight: 600, letterSpacing: ".18em",
                border: "none", cursor: "pointer", transition: "all .3s"
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gl)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--g)"; e.currentTarget.style.transform = "none"; }}
              >APPLY FOR COHORT 1 →</button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ APPLICATION PROCESS ═══════════════════════════════════════ */}
      <section style={{ padding: "80px 24px", borderBottom: "1px solid var(--gb)" }}>
        <div style={{ maxWidth: "var(--max)", margin: "0 auto" }}>
          <Reveal>
            <Tag>THE PROCESS</Tag>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(38px, 4vw, 58px)", fontWeight: 900, lineHeight: 1, marginBottom: 64 }}>
              20 Spots.<br /><span style={{ color: "var(--g5)", fontStyle: "normal" }}>Is One Yours?</span>
            </h2>
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 18px",
              marginBottom: 40,
              border: "1px solid var(--g)",
              background: "rgba(234,179,8,.08)",
              color: "var(--g)",
              fontFamily: "var(--sans)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: ".18em",
              textTransform: "uppercase",
              animation: "blinkGlow 1.8s ease-in-out infinite"
            }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--g)", animation: "pulse 1.1s ease-in-out infinite" }} />
              Cohort 1 kicks off July, 2026.
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", position: "relative" }}>
            <div style={{ position: "absolute", top: 19, left: "8%", right: "8%", height: 1, background: "linear-gradient(to right,var(--g),transparent)", opacity: .25, zIndex: 0 }} />
            {[
              { n: "01", title: "Apply", detail: "Fill out the application — takes around 10 minutes." },
              { n: "02", title: "Reviewed", detail: "Every application is read personally, within 48 hours." },
              { n: "03", title: "Discovery Call", detail: "A direct 20-min call with the lead founder." },
              { n: "04", title: "Confirmed", detail: "Secure your spot, payt the program fee, and we get started." },
            ].map((s, i) => (
              <Reveal key={i} delay={i * .1} style={{ position: "relative", zIndex: 1 }}>
                <div style={{ paddingRight: 28 }}>
                  <div style={{ width: 38, height: 38, border: "1px solid var(--g)", display: "flex", alignItems: "center", justifyContent: "center", background: "#080808", marginBottom: 26, fontFamily: "var(--sans)", fontSize: 11, color: "var(--g)", letterSpacing: ".1em", fontWeight: 500 }}>{s.n}</div>
                  <h4 style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{s.title}</h4>
                  <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", lineHeight: 1.7 }}>{s.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FAQ ══════════════════════════════════════════════════════ */}
      <section style={{ padding: "90px 24px", borderBottom: "1px solid var(--gb)" }}>
        <div style={{ maxWidth: "var(--max)", margin: "0 auto", display: "grid", gridTemplateColumns: "260px 1fr", gap: 80 }}>
          <Reveal>
            <Tag>COMMON QUESTIONS</Tag>
            <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.8 }}>
              Everything you need to decide if this is right for you.
            </p>
          </Reveal>
          <Reveal delay={.1}>
            <FAQ />
          </Reveal>
        </div>
      </section>

      {/* ══ FULL-BLEED CTA ════════════════════════════════════════════ */}
      <section id="apply" style={{ position: "relative", overflow: "hidden", minHeight: 520, display: "flex", alignItems: "center" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `repeating-linear-gradient(0deg,transparent,transparent 59px,var(--f) 60px),repeating-linear-gradient(90deg,transparent,transparent 59px,var(--f) 60px)` }} />
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 60% 50%,rgba(234,179,8,.08) 0%,transparent 65%)" }} />
        <div style={{ position: "relative", zIndex: 2, width: "100%", padding: "80px 24px", maxWidth: "var(--max)", margin: "0 auto" }}>
          <Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 60, alignItems: "center" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24, fontFamily: "var(--sans)", fontSize: 10, letterSpacing: ".3em", color: "var(--g)" }}>
                  <div style={{ width: 7, height: 7, background: "var(--g)", animation: "pulse 2s infinite" }} />
                  COHORT 01 · APPLICATIONS OPEN
                </div>
                <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(42px, 5vw, 68px)", fontWeight: 900, lineHeight: .95, letterSpacing: "-.02em", marginBottom: 24 }}>
                  Start Your<br /><span style={{ color: "var(--g5)", fontStyle: "normal" }}>Application.</span>
                </h2>
                <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", maxWidth: 460, lineHeight: 1.8 }}>
                  Reviewed personally within 48 hours. 20 spots. One cohort. If you've been waiting for the right moment — this is it.
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 240 }}>
                <button onClick={() => document.getElementById("applyform")?.scrollIntoView({ behavior: "smooth" })} style={{
                  display: "flex", alignItems: "center", gap: 16,
                  padding: "20px 32px", background: "var(--g)", color: "#080808",
                  fontFamily: "var(--sans)", fontSize: 11, fontWeight: 600, letterSpacing: ".18em",
                  border: "none", cursor: "pointer", transition: "all .3s", width: "100%"
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = "var(--gl)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "var(--g)"; e.currentTarget.style.transform = "none"; }}
                >APPLY NOW <span>→</span></button>
                <div style={{ fontFamily: "var(--sans)", fontSize: 12, color: "var(--m)", textAlign: "center" }}>AUD $5,000 · 20 spots only</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ APPLICATION FORM ══════════════════════════════════════════ */}
      <section id="applyform" style={{ padding: "90px 24px", background: "#050505" }}>
        <div style={{ maxWidth: 740, margin: "0 auto" }}>
          <Reveal>
            <Tag>YOUR APPLICATION</Tag>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(38px, 4vw, 58px)", fontWeight: 900, lineHeight: 1.05, marginBottom: 56 }}>
              Let's See If We're<br /><span style={{ color: "var(--g5)", fontStyle: "normal" }}>A Good Fit.</span>
            </h2>
          </Reveal>

          <Reveal delay={.05}>
            <FormSection num="01" label="ABOUT YOU *">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
                <div>
                  <input
                    placeholder="First Name *"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    data-error={!!fieldErrors.firstName}
                    style={fieldErrors.firstName ? { borderBottomColor: '#ef4444' } : {}}
                  />
                  {fieldErrors.firstName && <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "#ef4444", marginTop: 4, display: "block" }}>{fieldErrors.firstName}</span>}
                </div>
                <div>
                  <input
                    placeholder="Last Name *"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    data-error={!!fieldErrors.lastName}
                    style={fieldErrors.lastName ? { borderBottomColor: '#ef4444' } : {}}
                  />
                  {fieldErrors.lastName && <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "#ef4444", marginTop: 4, display: "block" }}>{fieldErrors.lastName}</span>}
                </div>
                <div>
                  <input
                    placeholder="Email Address *"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    data-error={!!fieldErrors.email}
                    style={fieldErrors.email ? { borderBottomColor: '#ef4444' } : {}}
                  />
                  {fieldErrors.email && <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "#ef4444", marginTop: 4, display: "block" }}>{fieldErrors.email}</span>}
                </div>
                <div>
                  <input
                    placeholder="Phone Number *"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    data-error={!!fieldErrors.phone}
                    style={fieldErrors.phone ? { borderBottomColor: '#ef4444' } : {}}
                  />
                  {fieldErrors.phone && <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "#ef4444", marginTop: 4, display: "block" }}>{fieldErrors.phone}</span>}
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <input
                    placeholder="Country / Location *"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    data-error={!!fieldErrors.country}
                    style={fieldErrors.country ? { borderBottomColor: '#ef4444' } : {}}
                  />
                  {fieldErrors.country && <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "#ef4444", marginTop: 4, display: "block" }}>{fieldErrors.country}</span>}
                </div>
              </div>
            </FormSection>
          </Reveal>

          <Reveal delay={.1}>
            <FormSection num="02" label="YOUR IDEA *">
              <div style={{ marginBottom: 32 }}>
                <input
                  placeholder="Your idea in one sentence *"
                  value={formData.idea}
                  onChange={(e) => handleInputChange('idea', e.target.value)}
                  data-error={!!fieldErrors.idea}
                  style={fieldErrors.idea ? { borderBottomColor: '#ef4444' } : {}}
                />
                {fieldErrors.idea && <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "#ef4444", marginTop: 4, display: "block" }}>{fieldErrors.idea}</span>}
              </div>
              <div style={{ marginBottom: 32 }}>
                <textarea
                  placeholder="What problem does it solve, and who has it? *"
                  rows={4}
                  value={formData.ideaDescription}
                  onChange={(e) => handleInputChange('ideaDescription', e.target.value)}
                  data-error={!!fieldErrors.ideaDescription}
                  style={fieldErrors.ideaDescription ? { borderBottomColor: '#ef4444' } : {}}
                />
                {fieldErrors.ideaDescription && <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "#ef4444", marginTop: 4, display: "block" }}>{fieldErrors.ideaDescription}</span>}
              </div>
              <div>
                <textarea
                  placeholder="What have you done so far? *"
                  rows={3}
                  value={formData.doneSoFar}
                  onChange={(e) => handleInputChange('doneSoFar', e.target.value)}
                  data-error={!!fieldErrors.doneSoFar}
                  style={fieldErrors.doneSoFar ? { borderBottomColor: '#ef4444' } : {}}
                />
                {fieldErrors.doneSoFar && <span style={{ fontFamily: "var(--sans)", fontSize: 11, color: "#ef4444", marginTop: 4, display: "block" }}>{fieldErrors.doneSoFar}</span>}
              </div>
            </FormSection>
          </Reveal>

          <Reveal delay={.15}>
            <FormSection num="03" label="YOUR STAGE">
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {["Pre-revenue idea stage", "Side hustle → Full time", "Corporate exec with an idea", "Early-stage startup"].map((s, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 20, padding: "20px 22px",
                    border: "1px solid", borderColor: stage === i ? "var(--g)" : "var(--gb)",
                    background: stage === i ? "var(--gdim)" : "transparent",
                    cursor: "pointer", transition: "all .25s",
                    fontFamily: "var(--sans)", fontSize: 14
                  }}
                    onClick={() => setStage(i)}
                    onMouseEnter={e => { if (stage !== i) e.currentTarget.style.borderColor = "rgba(250,204,21,.45)"; }}
                    onMouseLeave={e => { if (stage !== i) e.currentTarget.style.borderColor = "var(--gb)"; }}
                  >
                    <div style={{ width: 20, height: 20, borderRadius: "50%", border: "1px solid", borderColor: stage === i ? "var(--g)" : "var(--gb)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .25s" }}>
                      {stage === i && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--g)" }} />}
                    </div>
                    {s}
                  </div>
                ))}
              </div>
            </FormSection>
          </Reveal>

          <Reveal delay={.2}>
            <FormSection num="04" label="COMMITMENT & FIT">
              <textarea
                placeholder="Why now? What's the urgency for you?"
                rows={4}
                value={formData.urgency}
                onChange={(e) => handleInputChange('urgency', e.target.value)}
                style={{ marginBottom: 32 }}
              />
              <select
                value={formData.timeAvailability}
                onChange={(e) => handleInputChange('timeAvailability', e.target.value)}
                style={{ marginBottom: 32, color: formData.timeAvailability ? "var(--c)" : "var(--m)" }}
              >
                <option value="">Weekly time availability</option>
                <option>5–10 hours / week</option>
                <option>10–20 hours / week</option>
                <option>20+ hours / week</option>
              </select>
              <textarea
                placeholder="Anything else we should know?"
                rows={3}
                value={formData.anythingElse}
                onChange={(e) => handleInputChange('anythingElse', e.target.value)}
              />
            </FormSection>
          </Reveal>

          <Reveal delay={.25}>
            <div style={{ borderTop: "1px solid var(--gb)", paddingTop: 40, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 32 }}>
              <p style={{ fontFamily: "var(--sans)", fontSize: 12, color: "var(--m)", maxWidth: 280, lineHeight: 1.7 }}>
                Reviewed personally within 48 hours. Payment split available on acceptance.
              </p>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 16 }}>
                {submitMessage && (
                  <p style={{
                    fontFamily: "var(--sans)",
                    fontSize: 14,
                    color: submitMessage.includes('successfully') ? "var(--g)" : "#ef4444",
                    textAlign: "right",
                    maxWidth: 300
                  }}>
                    {submitMessage}
                  </p>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{
                    padding: "18px 48px",
                    background: isSubmitting ? "var(--gb)" : "var(--g)",
                    color: "#080808",
                    border: "none",
                    fontFamily: "var(--sans)",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: ".18em",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    transition: "all .3s",
                    whiteSpace: "nowrap",
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  onMouseEnter={e => {
                    if (!isSubmitting) {
                      e.currentTarget.style.background = "var(--gl)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isSubmitting) {
                      e.currentTarget.style.background = "var(--g)";
                      e.currentTarget.style.transform = "none";
                    }
                  }}
                >
                  {isSubmitting ? 'SUBMITTING...' : 'SUBMIT APPLICATION →'}
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FOOTER ═══════════════════════════════════════════════════ */}
      <footer style={{ padding: "28px 24px", borderTop: "1px solid var(--gb)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src="/logo.svg"
            alt="KPL Ventures Logo"
            style={{
              width: 46,
              height: 46,
              objectFit: "contain",
              display: "block",
              filter: "brightness(0) saturate(100%) invert(82%) sepia(42%) saturate(1198%) hue-rotate(352deg) brightness(102%) contrast(96%) drop-shadow(0 0 8px rgba(250,204,21,.25))"
            }}
          />
          <span style={{
            fontFamily: "var(--sans)", fontSize: 10, letterSpacing: ".22em", color: "var(--g)"
          }}>KPL VENTURES</span>
        </div>
        <span style={{ fontFamily: "var(--sans)", fontSize: 12, color: "var(--m)" }}>© 2026 KPL Ventures</span>
        <a href="https://kpl.ventures" className="gl" style={{ fontFamily: "var(--sans)", fontSize: 12, color: "var(--m)", textDecoration: "none" }}>kpl.ventures</a>
      </footer>
    </div>
  );
}
